/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useState, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label } from 'reactstrap';
import LoadingButton from '@g17eco/molecules/button/LoadingButton';
import { MultipleSelect } from '@g17eco/molecules/select/MultipleSelect';
import { toastFromError } from '@g17eco/molecules/toasts';
import { useGetSurveysForInitiativeQuery } from '@api/surveys';
import { useCreateGenerationRunMutation } from '@api/insights-synthesis';
import { getFormattedSurveyDate, isAggregatedSurvey } from '@utils/survey';
import { sortEffectiveDateDesc } from '@utils/sort';
import { SURVEY } from '@constants/terminology';
import type { SectionType, CreateGenerationRunRequest } from '@g17eco/types/insights-synthesis';
import type { SurveyListItem } from '@g17eco/types/survey';
import type { Option } from '@g17eco/molecules/select/types';

interface CreateRunModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiativeId: string;
}

const SECTION_TYPE_OPTIONS: Option<SectionType>[] = [
  { value: 'environment', label: 'Environment' },
  { value: 'social', label: 'Social' },
  { value: 'governance', label: 'Governance' },
  { value: 'economic', label: 'Economic' },
];

const EMPTY_SURVEYS: SurveyListItem[] = [];

const getSurveyIcon = (type: SurveyListItem['type'], completedDate?: string) => {
  if (isAggregatedSurvey(type)) {
    return 'fa fa-object-exclude mr-2';
  }
  return completedDate ? 'fa fa-check-circle mr-2' : 'fa fa-circle mr-2';
};

export const CreateRunModal = ({ isOpen, onClose, initiativeId }: CreateRunModalProps) => {
  const [selectedSurveyIds, setSelectedSurveyIds] = useState<string[]>([]);
  const [selectedSectionTypes, setSelectedSectionTypes] = useState<SectionType[]>([]);
  const { data, isLoading: isSurveysLoading } = useGetSurveysForInitiativeQuery(initiativeId);
  const surveys = data ?? EMPTY_SURVEYS;
  const [createRun, { isLoading: isCreating }] = useCreateGenerationRunMutation();

  const isFormValid = selectedSurveyIds.length > 0 && selectedSectionTypes.length > 0;

  const surveyOptions = useMemo(
    () =>
      [...surveys].sort(sortEffectiveDateDesc).map((survey) => {
        const displayText = getFormattedSurveyDate(survey);
        return {
          searchString: `${displayText} ${survey.name ?? ''}`,
          label: (
            <div className='d-flex flex-row align-items-center w-100 py-1'>
              <i className={getSurveyIcon(survey.type, survey.completedDate)} />
              <div className='flex-fill' style={{ minWidth: '0px' }}>
                <div className='fw-semibold text-truncate'>{displayText}</div>
                {survey.name ? <div className='fst-italic text-sm text-truncate'>{survey.name}</div> : null}
              </div>
            </div>
          ),
          value: survey._id,
          tooltip: `${displayText}${survey.name ? ` - ${survey.name}` : ''}`,
        };
      }),
    [surveys]
  );

  const handleSubmit = async () => {
    const data: CreateGenerationRunRequest = {
      surveyIds: selectedSurveyIds,
      sectionTypes: selectedSectionTypes!,
      clientId: initiativeId,
    };

    try {
      await createRun({ initiativeId, data }).unwrap();
      handleClose();
    } catch (err) {
      toastFromError({ error: err, title: 'Failed to start run' });
    }
  };

  const handleClose = () => {
    setSelectedSurveyIds([]);
    setSelectedSectionTypes([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size='lg'>
      <ModalHeader toggle={handleClose}>Start New Insights Synthesis Run</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label>{SURVEY.CAPITALIZED_PLURAL} (Optional)</Label>
          <MultipleSelect<string>
            options={surveyOptions}
            values={surveyOptions.filter((opt) => selectedSurveyIds.includes(opt.value)).map(o => o.value)}
            onChange={(values) => setSelectedSurveyIds(values)}
            placeholder={selectedSurveyIds.length > 0 ? `${selectedSurveyIds.length + 1} ${SURVEY.PLURAL} selected` : `Select ${SURVEY.PLURAL}...`}
            isDisabled={isSurveysLoading}
            isFullWidthOptions
          />
          <small className='text-muted'>
            Select specific {SURVEY.PLURAL} to generate insights for, or leave empty to process all {SURVEY.PLURAL}.
          </small>
        </FormGroup>

        <FormGroup>
          <Label>Section Type (Optional)</Label>
          <MultipleSelect<SectionType>
            options={SECTION_TYPE_OPTIONS}
            values={SECTION_TYPE_OPTIONS.filter((opt) => selectedSectionTypes.includes(opt.value)).map(o => o.value)}
            onChange={(options) => setSelectedSectionTypes(options as SectionType[])}
            placeholder={selectedSectionTypes.length > 0 ? `${selectedSectionTypes.length + 1} section types selected` : 'Select a section type...'}
          />
          <small className='text-muted'>
            Filter insights generation to a specific ESG section type, or leave empty to include all.
          </small>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <LoadingButton color='secondary' onClick={handleClose} disabled={isCreating}>
          Cancel
        </LoadingButton>
        <div style={{ width: '10rem' }}>
          <LoadingButton
            color='primary'
            onClick={handleSubmit}
            block={true}
            loading={isCreating}
            disabled={isCreating || !isFormValid}
          >
            Start Run
          </LoadingButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};
